import React from 'react';
import { useLocation } from 'react-router-dom';

import { useChat } from '../../contexts/ChatContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { FILLS, GRADIENTS, RADII, SHADOWS, SIZING, STROKES } from '../../styles/tokens';

const FAB_SIZE = 56;
const ICON_SIZE = 26;

/** Routes where the FAB is suppressed (detail + cart surfaces own the bottom). */
const isHiddenRoute = (pathname: string): boolean =>
  pathname.startsWith('/property') || pathname.startsWith('/service') || pathname === '/cart';

const fabStyle: React.CSSProperties = {
  position: 'fixed',
  right: 16,
  bottom: `calc(${SIZING.bottomNavHeight} + 16px)`,
  zIndex: 1100,
  width: FAB_SIZE,
  height: FAB_SIZE,
  borderRadius: RADII.pill,
  background: GRADIENTS.goldButton,
  border: `1px solid ${STROKES.goldCardStrong}`,
  boxShadow: SHADOWS.serviceHover,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
};

const unreadDotStyle: React.CSSProperties = {
  position: 'absolute',
  top: 2,
  right: 2,
  width: 12,
  height: 12,
  borderRadius: RADII.pill,
  background: FILLS.error,
  border: `2px solid ${FILLS.page}`,
};

/**
 * Mobile-only gold floating action button that opens the concierge chat sheet.
 * Sits above the bottom tab bar (`SIZING.bottomNavHeight + 16px`) at the
 * bottom-right. Self-gates: renders nothing on desktop and on detail/cart
 * routes (mockup shows `showFab={false}` there). Clicking opens the shared
 * chat (`openChat` from `useChat`); the mobile sheet renders the conversation.
 */
const ConciergeFab: React.FC = () => {
  const isMobile = useIsMobile();
  const { openChat } = useChat();
  const { pathname } = useLocation();

  if (!isMobile) return null;
  if (isHiddenRoute(pathname)) return null;

  return (
    <button type="button" aria-label="Open concierge" onClick={openChat} style={fabStyle}>
      <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M21 15C21 16.1 20.1 17 19 17H7L3 21V5C3 3.9 3.9 3 5 3H19C20.1 3 21 3.9 21 5V15Z"
          stroke={FILLS.page}
          strokeWidth="1.9"
          fill="none"
          strokeLinejoin="round"
        />
        <circle cx="8" cy="10" r="1" fill={FILLS.page} />
        <circle cx="12" cy="10" r="1" fill={FILLS.page} />
        <circle cx="16" cy="10" r="1" fill={FILLS.page} />
      </svg>
      <span style={unreadDotStyle} aria-hidden="true" />
    </button>
  );
};

export default ConciergeFab;
